import { useEffect, useState } from 'react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PasswordInput from '@/components/PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { USERS } from '@/constants/testIds';

const emptyForm = { username: '', name: '', password: '', role: 'user', email_aliases: [] };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const { data } = await api.get('/users');
      setItems(data);
    } catch (e) {
      if (e.response?.status === 403) {
        // Not admin
      } else {
        toast.error('Lataus epäonnistui');
      }
    }
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, name: u.name, password: '', role: u.role, email_aliases: u.email_aliases || [] });
    setOpen(true);
  };

  const save = async () => {
    try {
      const aliases = (Array.isArray(form.email_aliases)
        ? form.email_aliases
        : String(form.email_aliases || '').split(',')
      ).map((s) => s.trim().toLowerCase()).filter(Boolean);
      if (editing) {
        const payload = { name: form.name, role: form.role, email_aliases: aliases };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success('Tallennettu');
      } else {
        await api.post('/users', { ...form, email_aliases: aliases });
        toast.success('Käyttäjä lisätty');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Tallennus epäonnistui');
    }
  };

  const remove = async (u) => {
    if (!confirm(`Poistetaanko käyttäjä ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Hallinta</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Käyttäjät</h1>
          <p className="text-sm text-[#6A6D66] mt-1">Lisää tai poista käyttäjiä ja hallitse rooleja.</p>
        </div>
        <Button
          onClick={openNew}
          data-testid={USERS.addButton}
          className="h-9 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white"
        >
          <Plus size={16} className="mr-1" />
          Lisää käyttäjä
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F9F9F6] border-b border-[#E5E6E3] text-[#6A6D66]">
            <tr>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Nimi</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Käyttäjätunnus</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32">Rooli</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} data-testid={`${USERS.rowPrefix}${u.id}`} className="border-b border-[#E5E6E3] hover:bg-[#F9F9F6]">
                <td className="px-3 py-2 font-medium text-[#1C1C1A]">{u.name}</td>
                <td className="px-3 py-2 font-mono text-[#1C1C1A]">{u.username}</td>
                <td className="px-3 py-2">
                  {u.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#E8F0E9] text-[#2A4B30] font-medium">
                      <ShieldCheck size={12} /> Ylläpitäjä
                    </span>
                  ) : (
                    <span className="text-xs text-[#6A6D66]">Käyttäjä</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`${USERS.editPrefix}${u.id}`}
                      className="w-8 h-8 rounded-full hover:bg-[#F4F6F3] text-[#6A6D66] hover:text-[#1C1C1A] flex items-center justify-center"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      data-testid={`${USERS.deletePrefix}${u.id}`}
                      className="w-8 h-8 rounded-full hover:bg-[#FBEAE5] text-[#6A6D66] hover:text-[#C05A3C] flex items-center justify-center"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-sm text-[#6A6D66]">Ei käyttäjiä</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Muokkaa käyttäjää' : 'Lisää käyttäjä'}</DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Käyttäjät voivat muokata tilauksia. Ylläpitäjät hallitsevat lisäksi käyttäjiä ja asiakaspoistoja.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Nimi</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={USERS.dialogNameInput}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Käyttäjätunnus</Label>
              <Input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!!editing}
                placeholder="esim. pasi"
                autoCapitalize="off"
                spellCheck={false}
                data-testid={USERS.dialogUsernameInput}
              />
              {!editing && (
                <p className="text-[10px] text-[#6A6D66]">2–40 merkkiä, kirjaimet a–z, numerot, . _ - · ei välilyöntejä eikä sähköpostimuoto.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{editing ? 'Uusi salasana (jätä tyhjäksi jos ei muuteta)' : 'Salasana'}</Label>
              <PasswordInput
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid={USERS.dialogPasswordInput}
                autoComplete={editing ? 'new-password' : 'new-password'}
                toggleTestId="user-dialog-password-toggle"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rooli</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid={USERS.dialogRoleSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Käyttäjä</SelectItem>
                  <SelectItem value="admin">Ylläpitäjä</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sähköpostialiakset (pilkulla erotettuna)</Label>
              <Input
                data-testid="user-dialog-aliases-input"
                value={Array.isArray(form.email_aliases) ? form.email_aliases.join(', ') : form.email_aliases}
                onChange={(e) => setForm({ ...form, email_aliases: e.target.value })}
                placeholder="esim. ostot@firma.fi, ostotilaus@firma.fi"
              />
              <p className="text-[10px] text-[#6A6D66]">
                PDF-tilaukset sähköposteista näistä osoitteista merkitään tämän käyttäjän tekemiksi audit-lokiin.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Peruuta</Button>
            <Button onClick={save} data-testid={USERS.saveButton} className="bg-[#2A4B30] hover:bg-[#1E3622] text-white">Tallenna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
